import C from "assets/themes/colors";
import { IImageAttributes, IRawProps, IShapeCommonAttributes } from "@routes/private/Editor/types";
import { storageAccountName, containerName } from '@storage/azure-storage-blob';
import { loadImageAsync, rgbaToHex } from "./userUtils";
const blendModes = ['', 'source-over', 'source-in', 'source-out', 'source-atop', 'destination-over', 'destination-in', 'destination-out', 'destination-atop', 'lighter', 'copy', 'xor', 'multiply', 'screen', 'overlay', 'darken', 'lighten', 'color-dodge', 'color-burn', 'hard-light', 'soft-light', 'difference', 'exclusion', 'hue', 'saturation', 'color', 'luminosity'] as const;

interface measureResult {
  ascent: number;
  height: number;
  descent: number;
  width: number;
}

interface IDimensions {
  width: number;
  height: number;
}

export type blendModeTypes = typeof blendModes[number];

export const getMaskAndClipping = (
  ctx: CanvasRenderingContext2D | null, 
  maskImages: (HTMLImageElement | undefined)[],
  width: number, 
  height: number, 
  attributes: IImageAttributes | IShapeCommonAttributes
  ) => {
  if (attributes.rawProps?.length) {
    attributes.rawProps.map((rawProp, index) => {
      const maskImage = maskImages[index];
      if (ctx && maskImage && rawProp.mask?.left !== undefined && rawProp.mask?.right !== undefined &&
          rawProp.mask?.top !== undefined && rawProp.mask?.bottom !== undefined &&
          rawProp.mask?.disabled === false
      ) {
        var imageData = ctx.getImageData(0, 0, width, height);
        if (attributes.clipping === true) {
          ctx.clearRect(0, 0, width, height);
          ctx.fillStyle = "#000000";
          ctx.fillRect(0, 0, width, height);
          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(
            - attributes.x + rawProp.mask.left,
            - attributes.y + rawProp.mask.top,
            rawProp.mask.right - rawProp.mask.left,
            rawProp.mask.bottom - rawProp.mask.top
          );
        } else {
          ctx.clearRect(0, 0, width, height);
          ctx.fillStyle = rawProp.mask.defaultColor === 0 ? "#000000" : "#FFFFFF";
          ctx.fillRect(0, 0, width, height);
          ctx.drawImage(
            maskImage,
            - attributes.x + rawProp.mask.left,
            - attributes.y + rawProp.mask.top
          );
        }
        var mask_imageData = ctx.getImageData(0, 0, width, height);
        const data = imageData.data;
        const mask_data = mask_imageData.data;
        const isVector = rawProp.vector?.vectorFill?.type === "solid" ? true : false;
        for (var i = 0; i < data.length; i += 4) {
          data[i + 3] = data[i + 3] > 0 ?
            isVector ? 
              data[i + 3] * (mask_data[i] + mask_data[i + 1] + mask_data[i + 2]) * (1 - (data[i] + data[i + 1] + data[i + 2]) / 755) / 755
            : data[i + 3] * (mask_data[i] + mask_data[i + 1] + mask_data[i + 2]) / 755 
          : data[i + 3]; // opacity
        }
        ctx.putImageData(imageData, 0, 0);
      }
    });
  }
}

export const getBlendMode = (str: string | undefined) => {
  if (!str || str === 'normal') return '';
  let blendMode: blendModeTypes = '';
  const strIndex = blendModes.findIndex(blendMode => blendMode === str);
  if (strIndex >= 0) blendMode = blendModes[strIndex];
  return blendMode;
}

export const containImage = (ctx: CanvasRenderingContext2D | null, img: HTMLImageElement) => {
  if (ctx) {
    let canvas = ctx.canvas;
    let hRatio = canvas.width  / img.width;
    let vRatio =  canvas.height / img.height;
    let ratio  = Math.min ( hRatio, vRatio );
    let centerShift_x = (canvas.width - img.width*ratio) / 2;
    let centerShift_y = (canvas.height - img.height*ratio) / 2;  
    ctx.clearRect(0,0,canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, img.width, img.height, centerShift_x, centerShift_y, img.width*ratio, img.height*ratio);
  }
}

export const setMasks = async (rawProps: IRawProps[], folder: string, setMaskImages: Function) => {
  let newMaskImages: (HTMLImageElement | null)[] = Array(rawProps.length).fill(null);
  await Promise.all(rawProps.map(async (rawProp, index) => {
    if (rawProp.mask) {
      try {
        const maskImage = await loadImageAsync(`https://${storageAccountName}.blob.core.windows.net/${containerName}/${folder}/${rawProp.mask?.id}.png`, `Anonymous`);
        newMaskImages[index] = maskImage ?? null;
      } catch (error) {
        console.log(`Error loading mask image: ${error}`);
        newMaskImages[index] = null;
      }
    }
  }));
  setMaskImages(newMaskImages.filter(Boolean));
}

export const measureText = (text: string, fontSize: number|undefined, fontFamily: string|undefined) => {
  var span, block, div;

  span = document.createElement('span');
  block = document.createElement('div');
  div = document.createElement('div');

  block.style.display = 'inline-block';
  block.style.width = '1px';
  block.style.height = '0';

  div.style.visibility = 'hidden';
  div.style.position = 'absolute';
  div.style.top = '0';
  div.style.left = '0';
  div.style.width = '500px';
  div.style.height = '200px';

  div.appendChild(span);
  div.appendChild(block);
  document.body.appendChild(div);
  var result: measureResult | undefined = undefined;
  try {
      const size = (fontSize!==undefined?(fontSize.toString()+"px "):"");
      const family = (fontFamily!==undefined?fontFamily:"");
      const style = "font: " + size + family;
      span.setAttribute('style', style);
      span.innerHTML = '';
      span.appendChild(document.createTextNode(text.replace(/\s/g, String.fromCharCode(160))));

      block.style.verticalAlign = 'baseline';
      const ascent = (block.offsetTop - span.offsetTop);
      block.style.verticalAlign = 'bottom';
      const height = (block.offsetTop - span.offsetTop);
      const descent = height - ascent;
      const width = span.offsetWidth;
      result = {ascent, height, descent, width};
  } finally {
      div.parentNode?.removeChild(div);
      div = null;
  }
  return result;
}

export const drawEyeDropperPreview = (ctx: CanvasRenderingContext2D, data: Uint8ClampedArray, gridDim: IDimensions, canvasDim: IDimensions) => {
  let color: string | undefined = undefined;
  const width = Math.round(canvasDim.width / gridDim.width), height = Math.round(canvasDim.height / gridDim.height);
  const halfWidth = Math.round(width / 2), halfHeight = Math.round(height / 2);
  let x = -halfWidth, y = -halfHeight;
  let pointer = 0;
  for (let j = 0; j <= gridDim.height; j++) {
    for (let i = 0; i <= gridDim.width; i++) {
      ctx.beginPath();
      ctx.fillStyle = `rgb(${data[pointer]}, ${data[pointer + 1]}, ${data[pointer + 2]})`;
      ctx.rect(x, y, width, height);
      ctx.fill();
      pointer += 4;
      x += width;
      if (i === halfWidth - 1 && j === halfHeight) {
        if (data[pointer + 3] > 0)
        color = rgbaToHex({r: data[pointer], g: data[pointer + 1], b: data[pointer + 2]}) ?? C.DARK_BLUE;
      }
    }
    x = -halfWidth;
    y += height;
  }
  return color;
}